import { useState, useEffect } from 'react';
import { X, BookOpen, Video, ShoppingCart, Users, Bell, Sparkles } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const ACTIVITY_ICONS = {
  new_course: BookOpen,
  new_ebook: BookOpen,
  live_class_starting: Video,
  live_class_ongoing: Video,
  new_enrollment: Users,
  subscription_purchase: ShoppingCart,
  new_content: Sparkles,
  default: Bell
};

const ACTIVITY_COLORS = {
  new_course: 'from-indigo-500 to-purple-500',
  new_ebook: 'from-emerald-500 to-teal-500',
  live_class_starting: 'from-red-500 to-rose-500',
  live_class_ongoing: 'from-red-500 to-rose-500',
  new_enrollment: 'from-blue-500 to-cyan-500',
  subscription_purchase: 'from-amber-500 to-orange-500',
  new_content: 'from-pink-500 to-rose-500',
  default: 'from-slate-500 to-slate-600'
};

export default function ActivityPopup() {
  const [activities, setActivities] = useState([]);
  const [visible, setVisible] = useState([]);
  const [settings, setSettings] = useState({ enabled: true, types: [] });

  // Load activity notification settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await axios.get(`${API}/analytics/activity-settings`);
        setSettings(res.data);
      } catch {
        // Default settings if endpoint doesn't exist
        setSettings({
          enabled: true,
          types: ['new_course', 'new_ebook', 'live_class_starting', 'live_class_ongoing', 'subscription_purchase']
        });
      }
    };
    loadSettings();
  }, []);

  // Fetch recent activities periodically
  useEffect(() => {
    if (!settings.enabled) return;

    const fetchActivities = async () => {
      try {
        const res = await axios.get(`${API}/analytics/recent-activities`);
        const newActivities = res.data || [];
        
        // Filter by enabled types and show only recent (within last 30 seconds)
        const now = Date.now();
        const recentActivities = newActivities.filter(activity => {
          const activityTime = new Date(activity.timestamp).getTime();
          const isRecent = now - activityTime < 30000; // 30 seconds
          const isEnabled = settings.types.length === 0 || settings.types.includes(activity.type);
          const notShown = !visible.find(v => v.id === activity.id);
          return isRecent && isEnabled && notShown;
        });

        if (recentActivities.length > 0) {
          setVisible(prev => [...prev, ...recentActivities].slice(-3)); // Max 3 popups
        }
      } catch (error) {
        // Silently fail - don't spam console
      }
    };

    fetchActivities();
    const interval = setInterval(fetchActivities, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [settings, visible]);

  // Auto-dismiss popups after 5 seconds
  useEffect(() => {
    if (visible.length === 0) return;

    const timer = setTimeout(() => {
      setVisible(prev => prev.slice(1));
    }, 5000);

    return () => clearTimeout(timer);
  }, [visible]);

  const dismissPopup = (id) => {
    setVisible(prev => prev.filter(v => v.id !== id));
  };

  if (!settings.enabled || visible.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-3 max-w-sm" data-testid="activity-popup-container">
      {visible.map((activity, index) => {
        const IconComponent = ACTIVITY_ICONS[activity.type] || ACTIVITY_ICONS.default;
        const gradientClass = ACTIVITY_COLORS[activity.type] || ACTIVITY_COLORS.default;
        
        return (
          <div
            key={activity.id || index}
            className="animate-slide-in-right bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
            style={{
              animation: `slideInRight 0.3s ease-out ${index * 0.1}s both`
            }}
            data-testid={`activity-popup-${activity.type}`}
          >
            <div className={`h-1 bg-gradient-to-r ${gradientClass}`} />
            <div className="p-4 flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${gradientClass} flex items-center justify-center flex-shrink-0`}>
                <IconComponent className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {activity.title}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                  {activity.message}
                </p>
                {activity.action_url && (
                  <a
                    href={activity.action_url}
                    className="text-xs text-indigo-600 hover:underline mt-1 inline-block"
                  >
                    View details →
                  </a>
                )}
              </div>
              <button
                onClick={() => dismissPopup(activity.id)}
                className="text-slate-400 hover:text-slate-600 p-1 -mr-1 -mt-1"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}

      <style jsx>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}

// Admin component to manage activity popup settings
export function ActivityPopupSettings() {
  const [settings, setSettings] = useState({
    enabled: true,
    types: []
  });
  const [saving, setSaving] = useState(false);

  const activityTypes = [
    { id: 'new_course', label: 'New Course Added', icon: BookOpen },
    { id: 'new_ebook', label: 'New eBook Added', icon: BookOpen },
    { id: 'live_class_starting', label: 'Live Class Starting', icon: Video },
    { id: 'live_class_ongoing', label: 'Ongoing Live Classes', icon: Video },
    { id: 'new_enrollment', label: 'New Enrollments', icon: Users },
    { id: 'subscription_purchase', label: 'Subscription Purchases', icon: ShoppingCart },
  ];

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await axios.get(`${API}/analytics/activity-settings`);
        setSettings(res.data);
      } catch {
        // Use defaults
      }
    };
    loadSettings();
  }, []);

  const toggleType = (typeId) => {
    setSettings(prev => ({
      ...prev,
      types: prev.types.includes(typeId)
        ? prev.types.filter(t => t !== typeId)
        : [...prev.types, typeId]
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/analytics/activity-settings`, settings);
      alert('Settings saved!');
    } catch (error) {
      alert('Failed to save settings');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Activity Popup Notifications</h3>
          <p className="text-sm text-slate-400">Control which activities trigger popups for users</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
            className="w-5 h-5 rounded"
          />
          <span className="text-white">Enable Popups</span>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {activityTypes.map(type => {
          const IconComponent = type.icon;
          const isEnabled = settings.types.includes(type.id);
          
          return (
            <button
              key={type.id}
              type="button"
              onClick={() => toggleType(type.id)}
              className={`p-4 rounded-xl border text-left transition flex items-center gap-3 ${
                isEnabled
                  ? 'bg-indigo-500/20 border-indigo-500/50 text-white'
                  : 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:border-indigo-500/30'
              }`}
            >
              <IconComponent className={`w-5 h-5 ${isEnabled ? 'text-indigo-400' : 'text-slate-500'}`} />
              <span className="text-sm">{type.label}</span>
            </button>
          );
        })}
      </div>

      <button
        onClick={saveSettings}
        disabled={saving}
        className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium disabled:opacity-50"
      >
        {saving ? 'Saving...' : 'Save Settings'}
      </button>
    </div>
  );
}
